import React, { useEffect, useRef } from 'react';
import styles from './Banner.module.scss';
import { Typography } from '@/components/Atoms';

type BannerCountdownProps = {
  targetTime: Date; // The target time for the countdown
  mode: 'start' | 'end'; // Determines if it's counting down to start or end
  hasLabel: boolean;
  showCountdown: boolean;
  size?: 'small' | 'medium' | 'large';
  textColor?: string;
};

const BannerCountdown: React.FC<BannerCountdownProps> = ({ targetTime, mode, hasLabel, showCountdown, size = 'medium' }) => {
  const countdownRef = useRef<HTMLDivElement>(null);

  const calculateTimeLeft = (target: Date) => {
    const now = new Date();
    const timeLeft = Math.max(0, target?.getTime() - now.getTime());
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds, timeLeft };
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const { days, hours, minutes, seconds, timeLeft } = calculateTimeLeft(targetTime);

      if (showCountdown && timeLeft <= 0) {
        clearInterval(interval);
        // undo comment later
        if (countdownRef.current) {
          countdownRef.current.innerHTML = mode === 'start' ? 'Event Started!' : 'Event Ended!';
        }
        return;
      }

      if (countdownRef.current) {
        let countdownHTML = '';
        if (days > 0) {
          countdownHTML += `<div className='backdrop-blur-sm md:backdrop-blur-none'>${String(days).padStart(2, '0')}</div>:`; // Add days
        }
        if (days > 0 || hours > 0) {
          countdownHTML += `<div className='backdrop-blur-sm md:backdrop-blur-none'>${String(hours).padStart(2, '0')}</div>:`; // Add hours
        }
        countdownHTML += `<div className='backdrop-blur-sm md:backdrop-blur-none'>${String(minutes).padStart(2, '0')}</div>:<div className='backdrop-blur-sm md:backdrop-blur-none'>${String(seconds).padStart(2, '0')}</div>`; // Add minutes and seconds

        countdownRef.current.innerHTML = countdownHTML;
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTime, mode, showCountdown]); // Depend on targetTime, mode, and showCountdown

  const getSizeClass = () => {
    switch (size) {
      case 'small':
        return styles.small;
      case 'large':
        return styles.large;
      default:
        return styles.medium;
    }
  };

  if (!targetTime) return null;

  if (!targetTime) return null;
  return (
    <p className='flex flex-row items-center justify-center w-full gap-2 lg:w-fit lg:flex-col lg:items-start'>
      {showCountdown && (
        <>
          {hasLabel
            ? calculateTimeLeft(targetTime)?.timeLeft > 0 && <Typography size='s18ToS14'>{mode === 'start' ? 'Start In' : 'End In'}</Typography>
            : null}
          <div className={`${styles.countdown} ${getSizeClass()}`} ref={countdownRef} />
        </>
      )}
    </p>
  );
};

export default BannerCountdown;
